import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';

import { Text } from '@change/ds-core-ui';

import { memo } from '@change-corgi/core-react/core';
import { Translate } from '@change-corgi/core-react/i18n';

import type { PetitionContentUpdate } from '#src/app/pages/petitionDetails/shared/types';

type Props = Readonly<{
	update: PetitionContentUpdate;
}>;

export const UpdateBody = memo<Props>(function UpdateBody({ update }): JSX.Element | null {
	const textRef = useRef<HTMLDivElement>(null);
	const [displayText, setDisplayText] = useState<string | null>(null);
	const lastSizeRef = useRef('');

	// server-stripped already (PetitionUpdate.descriptionStripped) — no HTML tags to remove here
	const fullText = update.descriptionStripped || null;

	useEffect(() => {
		const el = textRef.current;
		if (!el || !fullText) return undefined;

		lastSizeRef.current = '';

		const recalc = () => {
			const sizeKey = `${el.clientWidth}x${el.clientHeight}`;
			if (sizeKey === lastSizeRef.current) return;
			lastSizeRef.current = sizeKey;

			el.textContent = fullText;
			const isOverflowing = el.scrollHeight > el.clientHeight;

			if (!isOverflowing) {
				setDisplayText(null);
				return;
			}

			const maxHeight = el.clientHeight;
			let lo = 0;
			let hi = fullText.length;

			while (lo < hi) {
				const mid = Math.ceil((lo + hi) / 2);
				el.textContent = `${fullText.slice(0, mid)}\u2026`;
				if (el.scrollHeight <= maxHeight) {
					lo = mid;
				} else {
					hi = mid - 1;
				}
			}

			const truncated = `${fullText.slice(0, lo)}\u2026`;
			el.textContent = truncated;
			setDisplayText(truncated);
		};

		recalc();

		if (typeof ResizeObserver === 'undefined') {
			return undefined;
		}

		const observer = new ResizeObserver(recalc);
		observer.observe(el);

		return () => observer.disconnect();
	}, [fullText]);

	if (!fullText) {
		return null;
	}

	return (
		<div>
			<Text
				as="div"
				ref={textRef}
				className="line-clamp-2 break-normal wrap-anywhere italic"
				expressive
				color="typography-primary"
				variant="body"
				size="medium"
			>
				{displayText ?? fullText}
			</Text>
			{displayText && (
				<Text className="text-accent-primary-500 mt-2 block text-sm underline">
					{update.type === 'dmResponse' ? (
						<Translate value="corgi.petition.details.updates.read_full_response" />
					) : (
						<Translate value="corgi.petition.details.updates.read_full_update" />
					)}
				</Text>
			)}
		</div>
	);
});
